import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { getCitationGroupByEnd } from './citationUtils';
import {
  anchorMetricsFromRect,
  computePopoverLayout,
  panelDimensionsFromElement,
  PopoverLayout,
} from './popoverPlacement';
import { useHoverZoneDismiss } from './useHoverZoneDismiss';
import { MessageCitation } from './webSearch.types';

interface PopoverState {
  citations: MessageCitation[];
  layout: PopoverLayout;
}

const DEFAULT_CITATION_LAYOUT: PopoverLayout = {
  placement: 'below',
  top: 0,
  left: 0,
  maxHeight: 320,
};

const layoutsMatch = (a: PopoverLayout, b: PopoverLayout): boolean =>
  a.top === b.top && a.left === b.left && a.maxHeight === b.maxHeight && a.placement === b.placement;

export const useCitationPopover = () => {
  const [popover, setPopover] = useState<PopoverState | null>(null);
  const activeChipRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const getAnchorRect = useCallback(() => activeChipRef.current?.getBoundingClientRect() ?? null, []);

  const handleClose = useCallback(() => {
    activeChipRef.current = null;
    setPopover(null);
  }, []);

  useHoverZoneDismiss({
    isOpen: popover !== null,
    onClose: handleClose,
    getAnchorRect,
    panelRef,
  });

  useLayoutEffect(() => {
    if (!popover || !activeChipRef.current || !panelRef.current) return;
    const triggerRect = activeChipRef.current.getBoundingClientRect();
    const { height: panelHeight, width: panelWidth } = panelDimensionsFromElement(panelRef.current);
    const layout = computePopoverLayout(anchorMetricsFromRect(triggerRect), panelHeight, panelWidth, 'below');
    setPopover((prev) => {
      if (!prev || layoutsMatch(prev.layout, layout)) return prev;
      return { ...prev, layout };
    });
  }, [popover]);

  const handleChipMouseEnter = useCallback((e: React.MouseEvent, messageCitations: MessageCitation[]) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('.citation-chip');
    if (!target || activeChipRef.current === target) return;
    activeChipRef.current = target;
    const end = parseInt(target.dataset.citationEnd ?? '', 10);
    if (isNaN(end)) return;
    const group = getCitationGroupByEnd(messageCitations, end);
    if (group.length === 0) return;
    const rect = target.getBoundingClientRect();
    const anchor = anchorMetricsFromRect(rect);
    setPopover({
      citations: group,
      layout: {
        placement: 'below',
        top: anchor.bottom,
        left: anchor.left,
        maxHeight: DEFAULT_CITATION_LAYOUT.maxHeight,
      },
    });
  }, []);

  return { popover, handleChipMouseEnter, panelRef };
};
